import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  setManifestForChatbotFiltersModal,
  setManifestStatusForChatbotFiltersModal,
} from '../../actions/chatbotFiltersModal';
import { chatbotFiltersModalSelector } from '../../selectors/chatbotFiltersModalSelector';
import { fetchChatbotFiltersManifest } from '../../services/сhatbot/fetchChatbotFiltersManifest';
import { IChatbotFilters, IChatbotFiltersApi } from '../../types/chatbotFilters';
import { EManifestStatus } from '../../types/chatbotFiltersModal';
import { IMicrofrontendManifest } from '../../types/microfrontendManifest';
import { useApplicationContext } from '../../utils/applicationContext';

import type { ICianMFUIElement } from '@cian/mf-registry/browser';

type TReturnValue = {
  manifest: IMicrofrontendManifest | null;
  manifestStatus: EManifestStatus;
  fetchManifest: () => Promise<void>;
  getLocalApi: () => IChatbotFiltersApi;
  microfrontendRef: React.RefObject<ICianMFUIElement>;
};

/**
 * Хук для работы с инфраструктурой микрофронта
 */
export const useChatbotFilters = (): TReturnValue => {
  const context = useApplicationContext();
  const { manifest, manifestStatus, isOpened } = useSelector(chatbotFiltersModalSelector);
  const microfrontendRef = React.useRef<ICianMFUIElement>(null);
  const dispatch = useDispatch();

  const getLocalApi = React.useCallback(() => {
    const api = (microfrontendRef.current?.instance?.customElement as IChatbotFilters)?.api;
    if (!api) {
      throw new Error('Не удалось получить доступ к локальному API микрофронтенда "chatbot-filters-microfrontend"');
    }

    return api;
  }, []);

  const fetchManifest = React.useCallback(async () => {
    dispatch(setManifestStatusForChatbotFiltersModal(EManifestStatus.Loading));

    const manifest = await fetchChatbotFiltersManifest(context);

    if (!manifest) {
      dispatch(setManifestStatusForChatbotFiltersModal(EManifestStatus.Fail));

      return;
    }

    dispatch(setManifestStatusForChatbotFiltersModal(EManifestStatus.Success));
    dispatch(setManifestForChatbotFiltersModal({ manifest }));
  }, [context, dispatch]);

  React.useEffect(() => {
    if (!manifest && isOpened && manifestStatus !== EManifestStatus.Loading) {
      fetchManifest();
    }
  }, [manifest, isOpened, manifestStatus, fetchManifest]);

  return React.useMemo(
    () => ({
      manifest,
      manifestStatus,
      fetchManifest,
      getLocalApi,
      microfrontendRef,
    }),
    [manifest, manifestStatus, fetchManifest, getLocalApi],
  );
};
